import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { apiService } from '../services/api';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(1rem, 3vw, 1.25rem);
`;

const PageTitle = styled.h1`
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div`
  background: ${props => props.$isDarkMode ? '#2d2d2d' : 'white'};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: ${props => props.$isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  border: ${props => props.$isDarkMode ? '1px solid #404040' : 'none'};
`;

const SectionTitle = styled.h2`
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #f0f0f0'};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 14px;
`;

const Select = styled.select`
  padding: 12px;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e1e5e9'};
  border-radius: 8px;
  font-size: 16px;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  width: 100%;
  max-width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  }
  
  &:disabled {
    background: ${props => props.$isDarkMode ? '#333333' : '#f8f9fa'};
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px;
  }
  
  option {
    font-size: inherit;
    
    @media (max-width: 480px) {
      font-size: 12px;
    }
  }
`;

const Input = styled.input`
  padding: 12px;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e1e5e9'};
  border-radius: 8px;
  font-size: 16px;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  width: 100%;
  max-width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  }
  
  &:disabled {
    background: ${props => props.$isDarkMode ? '#333333' : '#f8f9fa'};
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e1e5e9'};
  border-radius: 8px;
  font-size: 16px;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  resize: vertical;
  min-height: 80px;
  width: 100%;
  max-width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px;
    min-height: 70px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px;
    min-height: 60px;
  }
`;

const Button = styled.button`
  background: ${props => 
    props.variant === 'secondary' 
      ? (props.$isDarkMode ? '#555555' : '#6c757d')
      : (props.$isDarkMode ? '#4dabf7' : '#007bff')
  };
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => 
      props.variant === 'secondary' 
        ? (props.$isDarkMode ? '#444444' : '#5a6268')
        : (props.$isDarkMode ? '#339af0' : '#0056b3')
    };
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    > button {
      width: 100%;
    }
  }
`;

const AccountCard = styled.div`
  background: ${props => props.selected ? '#e3f2fd' : '#f8f9fa'};
  border: 2px solid ${props => props.selected ? '#2196f3' : '#e1e5e9'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2196f3;
    background: #e3f2fd;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
`;

const AccountDetails = styled.div`
  flex: 1;
`;

const AccountNumber = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const AccountType = styled.div`
  color: #666;
  font-size: 14px;
`;

const AccountBalance = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #28a745;
`;

const TransferCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => 
    props.status === 'COMPLETED' ? '#28a745' : 
    props.status === 'PENDING' ? '#ffc107' : '#dc3545'
  };
`;

const TransferHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TransferAmount = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #333;
`;

const TransferStatus = styled.span`
  background: ${props => 
    props.status === 'COMPLETED' ? '#28a745' : 
    props.status === 'PENDING' ? '#ffc107' : '#dc3545'
  };
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const TransferDetails = styled.div`
  color: #666;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: ${props => props.$isDarkMode ? '#3a1e1e' : '#f8d7da'};
  color: ${props => props.$isDarkMode ? '#ff6b6b' : '#721c24'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: ${props => props.$isDarkMode ? '1px solid #ff6b6b' : '1px solid #f5c6cb'};
`;

const SuccessMessage = styled.div`
  background: ${props => props.$isDarkMode ? '#1e3a1e' : '#d4edda'};
  color: ${props => props.$isDarkMode ? '#51cf66' : '#155724'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: ${props => props.$isDarkMode ? '1px solid #51cf66' : '1px solid #c3e6cb'};
`;

const LoadingSpinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TransferTypeSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TransferTypeButton = styled.button`
  padding: 16px;
  border: 2px solid ${props => 
    props.selected 
      ? (props.$isDarkMode ? '#4dabf7' : '#007bff')
      : (props.$isDarkMode ? '#404040' : '#e1e5e9')
  };
  background: ${props => 
    props.selected 
      ? (props.$isDarkMode ? '#1e2a3a' : '#e3f2fd')
      : (props.$isDarkMode ? '#2d2d2d' : 'white')
  };
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
    background: ${props => props.$isDarkMode ? '#1e2a3a' : '#e3f2fd'};
  }
`;

const ExternalAccountInfo = styled.div`
  background: ${props => props.$isDarkMode ? '#1e3a1e' : '#e8f5e8'};
  border: ${props => props.$isDarkMode ? '1px solid #51cf66' : '1px solid #28a745'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
`;

const FeeInfo = styled.div`
  background: ${props => props.$isDarkMode ? '#3a3020' : '#fff3cd'};
  border: ${props => props.$isDarkMode ? '1px solid #ffd43b' : '1px solid #ffc107'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  
  h4 {
    margin: 0 0 8px 0;
    color: ${props => props.$isDarkMode ? '#ffd43b' : '#856404'};
  }
  
  p {
    margin: 4px 0;
    color: ${props => props.$isDarkMode ? '#ffffff' : '#856404'};
  }
`;

const LimitsInfo = styled.div`
  background: ${props => props.$isDarkMode ? '#1e1e1e' : '#f8f9fa'};
  border-radius: 8px;
  padding: 16px;
  border: ${props => props.$isDarkMode ? '1px solid #404040' : 'none'};
`;

const LimitsTitle = styled.h3`
  margin: 0 0 12px 0;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#495057'};
`;

const LimitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LimitItem = styled.div`
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 14px;
`;

function TransfersPage() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { refreshNotifications } = useNotifications();
  
  // Transfer type: 'MY_ACCOUNT' or 'EXTERNAL_ACCOUNT'
  const [transferType, setTransferType] = useState('MY_ACCOUNT');
  
  // Form data
  const [transferForm, setTransferForm] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: ''
  });
  
  // External account data
  const [externalAccountNumber, setExternalAccountNumber] = useState('');
  const [externalAccountInfo, setExternalAccountInfo] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  
  // Application state
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [limits, setLimits] = useState(null);
  const [remainingLimits, setRemainingLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchingAccount, setSearchingAccount] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Reset form when transfer type changes
    setTransferForm({
      fromAccountId: '',
      toAccountId: '',
      amount: '',
      description: ''
    });
    setExternalAccountNumber('');
    setExternalAccountInfo(null);
    setFeeInfo(null);
    setError('');
    setSuccess('');
  }, [transferType]);

  useEffect(() => {
    // Calculate fee when accounts and amount are selected
    if (transferForm.fromAccountId && transferForm.toAccountId && transferForm.amount) {
      calculateFee();
    }
  }, [transferForm.fromAccountId, transferForm.toAccountId, transferForm.amount]);

  useEffect(() => {
    // Auto-search external account when account number is entered
    if (transferType === 'EXTERNAL_ACCOUNT' && externalAccountNumber.trim().length >= 10) {
      const timeoutId = setTimeout(() => {
        searchExternalAccount();
      }, 500); // Debounce for 500ms
      
      return () => clearTimeout(timeoutId);
    } else if (externalAccountNumber.trim().length < 10) {
      setExternalAccountInfo(null);
      setTransferForm(prev => ({ ...prev, toAccountId: '' }));
    }
  }, [externalAccountNumber, transferType]);

  const loadData = async () => {
    setLoading(true);
    try {
      const isAdmin = user.role === 'ADMIN' || user.role === 'SUPPORT';
      
      const [accountsRes, transfersRes, limitsRes, remainingLimitsRes, allAccountsRes] = await Promise.all([
        apiService.getAccounts(),
        apiService.getTransfers({ limit: 10 }),
        apiService.getTransferLimits(),
        apiService.getRemainingLimits(),
        isAdmin ? apiService.getAllAccounts().catch(() => ({ accounts: [] })) : Promise.resolve({ accounts: [] })
      ]);
      
      setAccounts(accountsRes.accounts || []);
      setAllAccounts(allAccountsRes.accounts || []);
      setTransfers(transfersRes.transfers || []);
      setLimits(limitsRes.limits || null);
      setRemainingLimits(remainingLimitsRes.limits || null);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchExternalAccount = async () => {
    if (!externalAccountNumber.trim()) {
      setError('Please enter an account number');
      return;
    }

    setSearchingAccount(true);
    setError('');
    
    try {
      const result = await apiService.searchAccountByNumber(externalAccountNumber.trim());
      
      if (result.account.isOwnAccount) {
        setError('This is your own account. Please use "Transfer to My Account" option instead.');
        setExternalAccountInfo(null);
        return;
      }
      
      setExternalAccountInfo(result.account);
      setTransferForm(prev => ({
        ...prev,
        toAccountId: result.account.id
      }));
      
    } catch (err) {
      setError('Account not found: ' + err.message);
      setExternalAccountInfo(null);
    } finally {
      setSearchingAccount(false);
    }
  };

  const calculateFee = async () => {
    try {
      const result = await apiService.getFeeInfo(transferForm.fromAccountId, transferForm.toAccountId);
      setFeeInfo(result.feeInfo);
    } catch (err) {
      console.error('Failed to calculate fee:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setTransferForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!transferForm.fromAccountId) {
      setError('Please select a source account');
      return false;
    }
    
    if (transferType === 'MY_ACCOUNT') {
      if (!transferForm.toAccountId) {
        setError('Please select a destination account');
        return false;
      }
      if (transferForm.fromAccountId === transferForm.toAccountId) {
        setError('Source and destination accounts must be different');
        return false;
      }
    } else {
      if (!externalAccountInfo) {
        setError('Please search and confirm the external account');
        return false;
      }
    }
    
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    
    const amount = parseFloat(transferForm.amount);
    const feeAmount = feeInfo ? feeInfo.amount : 0;
    const totalAmount = amount + feeAmount;
    
    // Check per-transaction limit
    if (remainingLimits && remainingLimits.perTransaction.limit !== null && amount > remainingLimits.perTransaction.limit) {
      setError(`Amount exceeds your per-transaction limit of ${formatAmount(remainingLimits.perTransaction.limit)}`);
      return false;
    }
    
    // Check daily limit
    if (remainingLimits && remainingLimits.daily.limit !== null && amount > remainingLimits.daily.remaining) {
      setError(`Amount exceeds your remaining daily limit of ${formatAmount(remainingLimits.daily.remaining)}`);
      return false;
    }
    
    // Check monthly limit
    if (remainingLimits && remainingLimits.monthly.limit !== null && amount > remainingLimits.monthly.remaining) {
      setError(`Amount exceeds your remaining monthly limit of ${formatAmount(remainingLimits.monthly.remaining)}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const transferData = {
        fromAccountId: transferForm.fromAccountId,
        toAccountId: transferForm.toAccountId,
        amount: parseFloat(transferForm.amount),
        description: transferForm.description
      };
      
      const result = await apiService.createTransfer(transferData);
      
      const feeMessage = result.feeInfo && result.feeInfo.amount > 0 
        ? ` (Fee: $${result.feeInfo.amount})` 
        : '';
      
      setSuccess(`Transfer completed successfully!${feeMessage} Reference: ${result.transfer.reference_number}`);
      
      // 🔔 ВАЖНО: Принудительно обновляем уведомления после перевода
      console.log('[TransfersPage] Triggering notification refresh after successful transfer');
      refreshNotifications(1500); // Задержка 1.5 секунды для backend
      
      // Reset form
      setTransferForm({
        fromAccountId: '',
        toAccountId: '',
        amount: '',
        description: ''
      });
      setExternalAccountNumber('');
      setExternalAccountInfo(null);
      setFeeInfo(null);
      
      // Reload data and refresh remaining limits
      loadData();
    } catch (err) {
      setError('Transfer failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccountLabel = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId) || 
                   allAccounts.find(acc => acc.id === accountId);
    return account ? `${account.account_number} (${account.account_type})` : 'Unknown Account';
  };

  const formatAmount = (amount) => {
    if (amount === Infinity || amount === null || amount === undefined) {
      return 'Unlimited';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatAccountOption = (account) => {
    const isMobile = window.innerWidth < 480;
    if (isMobile) {
      // Shorter format for mobile: "...1234 $500.00"
      const lastFour = account.account_number.slice(-4);
      return `...${lastFour} ${formatAmount(account.balance)}`;
    }
    // Full format for desktop
    return `${account.account_number} (${account.account_type}) - ${formatAmount(account.balance)}`;
  };

  return (
    <PageContainer data-testid="transfers-page">
      <PageTitle $isDarkMode={isDarkMode}>
        <span>🔄 Money Transfers</span>
      </PageTitle>



      {/* Transfer Form */}
      <Section $isDarkMode={isDarkMode}>
        <SectionTitle $isDarkMode={isDarkMode}>Create New Transfer</SectionTitle>
        
        {error && <ErrorMessage $isDarkMode={isDarkMode}>{error}</ErrorMessage>}
        {success && <SuccessMessage $isDarkMode={isDarkMode}>{success}</SuccessMessage>}
        
        {/* Transfer Type Selector */}
        <TransferTypeSelector>
          <TransferTypeButton 
            $isDarkMode={isDarkMode}
            type="button"
            selected={transferType === 'MY_ACCOUNT'}
            onClick={() => setTransferType('MY_ACCOUNT')}
          >
            📱 Transfer to My Account<br/>
            <small>Between your own accounts (No fees)</small>
          </TransferTypeButton>
          <TransferTypeButton 
            $isDarkMode={isDarkMode}
            type="button"
            selected={transferType === 'EXTERNAL_ACCOUNT'}
            onClick={() => setTransferType('EXTERNAL_ACCOUNT')}
          >
            🏦 Transfer to External Account<br/>
            <small>To other users' accounts (May include fees)</small>
          </TransferTypeButton>
        </TransferTypeSelector>
        
        <form onSubmit={handleSubmit} data-testid="transfer-form">
          <FormGrid>
            {/* From Account */}
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>From Account</Label>
              <Select
                $isDarkMode={isDarkMode}
                data-testid="from-account-select"
                value={transferForm.fromAccountId}
                onChange={(e) => handleInputChange('fromAccountId', e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select source account</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {formatAccountOption(account)}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {/* To Account - Dynamic based on transfer type */}
            {transferType === 'MY_ACCOUNT' ? (
              <FormGroup>
                <Label $isDarkMode={isDarkMode}>To My Account</Label>
                <Select
                  $isDarkMode={isDarkMode}
                  data-testid="to-account-select"
                  value={transferForm.toAccountId}
                  onChange={(e) => handleInputChange('toAccountId', e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Select destination account</option>
                  {accounts.filter(acc => acc.id !== transferForm.fromAccountId).map(account => (
                    <option key={account.id} value={account.id}>
                      {formatAccountOption(account)}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label $isDarkMode={isDarkMode}>External Account Number</Label>
                <Input
                  $isDarkMode={isDarkMode}
                  type="text"
                  value={externalAccountNumber}
                  onChange={(e) => setExternalAccountNumber(e.target.value)}
                  placeholder="Enter account number (minimum 10 characters)"
                  disabled={loading}
                />
                {searchingAccount && (
                  <div style={{ fontSize: '14px', color: isDarkMode ? '#4dabf7' : '#007bff', marginTop: '4px' }}>
                    🔍 Searching for account...
                  </div>
                )}
              </FormGroup>
            )}
          </FormGrid>

          {/* External Account Info */}
          {externalAccountInfo && (
            <ExternalAccountInfo $isDarkMode={isDarkMode}>
              <h4>✅ Account Found</h4>
              <p><strong>Account:</strong> {externalAccountInfo.accountNumber} ({externalAccountInfo.accountType})</p>
              <p><strong>Owner:</strong> {externalAccountInfo.ownerName}</p>
            </ExternalAccountInfo>
          )}

          <FormGrid>
            {/* Amount */}
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>Amount ($)</Label>
              <Input
                $isDarkMode={isDarkMode}
                data-testid="amount-input"
                type="number"
                step="0.01"
                min="0"
                value={transferForm.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="0.00"
                disabled={loading}
                required
              />
            </FormGroup>

            {/* Description */}
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>Description (Optional)</Label>
              <TextArea
                $isDarkMode={isDarkMode}
                data-testid="description-input"
                value={transferForm.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Purpose of transfer..."
                disabled={loading}
              />
            </FormGroup>
          </FormGrid>

          {/* Transfer Information (Fee + Limits) */}
          {(feeInfo || (remainingLimits && transferForm.amount && parseFloat(transferForm.amount) > 0)) && (
            <FeeInfo $isDarkMode={isDarkMode}>
              <h4>💰 Transfer Information</h4>
              
              {/* Fee Section */}
              {feeInfo && (
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e1e5e9'}` }}>
                  <h5 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#4dabf7' : '#007bff' }}>Fee Details</h5>
                  <p><strong>Transfer Type:</strong> {feeInfo.type}</p>
                  <p><strong>Fee Amount:</strong> {formatAmount(feeInfo.amount)}</p>
                  <p><strong>Total Deduction:</strong> {formatAmount(parseFloat(transferForm.amount || 0) + feeInfo.amount)}</p>
                  <p><em>{feeInfo.description}</em></p>
                </div>
              )}

              {/* Limits Section */}
              {remainingLimits && transferForm.amount && parseFloat(transferForm.amount) > 0 && (
                <div>
                  <h5 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#4dabf7' : '#007bff' }}>📊 Remaining Limits ({user.role})</h5>
                  <p><strong>Per Transaction:</strong> {
                    remainingLimits.perTransaction.limit === null ? 'Unlimited' : 
                    formatAmount(Math.max(0, remainingLimits.perTransaction.remaining - parseFloat(transferForm.amount || 0)))
                  }</p>
                  <p><strong>Daily Limit:</strong> {
                    remainingLimits.daily.limit === null ? 'Unlimited' : 
                    `${formatAmount(Math.max(0, remainingLimits.daily.remaining - parseFloat(transferForm.amount || 0)))} (Used: ${formatAmount(remainingLimits.daily.used)})`
                  }</p>
                  <p><strong>Monthly Limit:</strong> {
                    remainingLimits.monthly.limit === null ? 'Unlimited' : 
                    `${formatAmount(Math.max(0, remainingLimits.monthly.remaining - parseFloat(transferForm.amount || 0)))} (Used: ${formatAmount(remainingLimits.monthly.used)})`
                  }</p>
                </div>
              )}
            </FeeInfo>
          )}

          <ButtonGroup>
            <Button $isDarkMode={isDarkMode} type="submit" disabled={loading || (transferType === 'EXTERNAL_ACCOUNT' && !externalAccountInfo)}>
              {loading ? 'Processing...' : 'Transfer Money'}
            </Button>
          </ButtonGroup>
        </form>
      </Section>

      {/* Recent Transfers - keeping existing display logic */}
      <Section $isDarkMode={isDarkMode}>
        <SectionTitle $isDarkMode={isDarkMode}>Recent Transfers</SectionTitle>
        {transfers.length === 0 ? (
          <p style={{ color: isDarkMode ? '#b3b3b3' : '#333' }}>No transfers found.</p>
        ) : (
          transfers.map(transfer => (
            <div key={transfer.id} style={{ 
              background: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
              padding: '16px', 
              marginBottom: '12px', 
              borderRadius: '8px',
              borderLeft: `4px solid ${transfer.status === 'COMPLETED' ? '#28a745' : transfer.status === 'PENDING' ? '#ffc107' : '#dc3545'}`,
              border: isDarkMode ? '1px solid #404040' : 'none',
              color: isDarkMode ? '#ffffff' : '#333'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: '700', fontSize: '18px' }}>
                  {formatAmount(transfer.amount)}
                  {(transfer.fee_amount && transfer.fee_amount > 0) && (
                    <span style={{ color: '#dc3545', fontSize: '14px' }}> + {formatAmount(transfer.fee_amount)} fee</span>
                  )}
                  {(transfer.transfer_type_classification === 'EXTERNAL' && (!transfer.fee_amount || transfer.fee_amount === 0)) && (
                    <span style={{ color: '#28a745', fontSize: '14px' }}> (No fee)</span>
                  )}
                </div>
                <div style={{ 
                  padding: '4px 8px', 
                  borderRadius: '4px', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  background: transfer.status === 'COMPLETED' ? '#d4edda' : transfer.status === 'PENDING' ? '#fff3cd' : '#f8d7da',
                  color: transfer.status === 'COMPLETED' ? '#155724' : transfer.status === 'PENDING' ? '#856404' : '#721c24'
                }}>
                  {transfer.status}
                </div>
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                From: {transfer.from_account_number} → To: {transfer.to_account_number}
                {transfer.transfer_type_classification && (
                  <span style={{ 
                    marginLeft: '8px', 
                    fontSize: '12px', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    background: transfer.transfer_type_classification === 'INTERNAL' ? '#e3f2fd' : '#fff3cd',
                    color: transfer.transfer_type_classification === 'INTERNAL' ? '#0277bd' : '#856404'
                  }}>
                    {transfer.transfer_type_classification === 'INTERNAL' ? '📱 Internal' : '🏦 External'}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {new Date(transfer.created_at).toLocaleDateString()} • Ref: {transfer.reference_number}
              </div>
              {transfer.description && (
                <div style={{ fontSize: '14px', marginTop: '8px', fontStyle: 'italic' }}>
                  {transfer.description}
                </div>
              )}
            </div>
          ))
        )}
      </Section>
    </PageContainer>
  );
}

export default TransfersPage; 